<template>
  <div :class="['shows-page', isHome ? 'py-8' : 'bg-base-200 min-h-screen']">
    <div :class="['container mx-auto', isHome ? '' : 'p-4 md:p-8']">
      <div :class="[isHome ? '' : 'card bg-base-100 shadow-xl lg:p-8']">
        <div :class="isHome ? '' : 'card-body'">
          <div class="flex flex-col justify-center items-center mb-6 gap-4">
            <h2 
              class="text-4xl font-bold text-center text-primary"
              :class="{ 'card-title': !isHome }"
            >
              Upcoming Shows
            </h2>
          </div>

          <div class="bandsintown-widget-container w-full max-w-5xl mx-auto px-2 md:px-0 bg-white rounded-xl pb-4 pt-2">
            <div id="bandsintown-widget-root">
              <a
                class="bit-widget-initializer"
                data-artist-name="sqwerv"
                data-display-local-dates="false"
                data-display-past-dates="false"
                data-auto-style="false"
                data-font="sans-serif"
                data-text-color="rgba(31,41,55,1)"
                data-link-color="rgba(20,184,166,1)"
                data-background-color="rgba(0,0,0,0)"
                data-display-limit="15"
                data-display-lineup="false"
                data-display-play-my-city="true"
                data-separator-color="rgba(209,213,219,1)"
                data-event-ticket-cta-bg-color="rgba(20,184,166,1)"
                data-event-ticket-cta-border-color="rgba(20,184,166,1)"
                data-event-ticket-cta-text-color="rgba(255,255,255,1)"
                data-event-ticket-cta-border-radius="8px"
                data-event-rsvp-cta-bg-color="rgba(255,255,255,1)"
                data-event-rsvp-cta-border-color="rgba(20,184,166,1)"
                data-event-rsvp-cta-text-color="rgba(20,184,166,1)"
                data-event-rsvp-cta-border-radius="8px"
                data-follow-section-cta-bg-color="rgba(20,184,166,1)"
                data-follow-section-cta-border-color="rgba(20,184,166,1)"
                data-follow-section-cta-text-color="rgba(255,255,255,1)"
                data-follow-section-cta-border-radius="8px"
              ></a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'

const route = useRoute()
const isHome = computed(() => route.name === 'home')

useHead({
  title: 'Upcoming Shows - Sqwerv',
  meta: [
    {
      hid: 'sqwervShowsPage',
      name: 'description',
      content:
        'Sqwerv shows page. See what shows Sqwerv has coming up so you can come checkout the action.',
    },
  ],
})

onMounted(() => {
  // Always inject a fresh script to ensure the widget re-renders when navigating back in Vue
  const oldScript = document.getElementById('bandsintown-script')
  if (oldScript) {
    oldScript.remove()
  }

  // When Bandsintown script runs, it modifies the DOM around the initializer.
  // To ensure it can run multiple times in an SPA, we need to recreate the initializer tag
  const root = document.getElementById('bandsintown-widget-root')
  if (root) {
    root.innerHTML = `
      <a
        class="bit-widget-initializer"
        data-artist-name="sqwerv"
        data-display-local-dates="false"
        data-display-past-dates="false"
        data-auto-style="false"
        data-font="sans-serif"
        data-text-color="rgba(31,41,55,1)"
        data-link-color="rgba(20,184,166,1)"
        data-background-color="rgba(0,0,0,0)"
        data-display-limit="15"
        data-display-lineup="false"
        data-display-play-my-city="true"
        data-separator-color="rgba(209,213,219,1)"
        data-event-ticket-cta-bg-color="rgba(20,184,166,1)"
        data-event-ticket-cta-border-color="rgba(20,184,166,1)"
        data-event-ticket-cta-text-color="rgba(255,255,255,1)"
        data-event-ticket-cta-border-radius="8px"
        data-event-rsvp-cta-bg-color="rgba(255,255,255,1)"
        data-event-rsvp-cta-border-color="rgba(20,184,166,1)"
        data-event-rsvp-cta-text-color="rgba(20,184,166,1)"
        data-event-rsvp-cta-border-radius="8px"
        data-follow-section-cta-bg-color="rgba(20,184,166,1)"
        data-follow-section-cta-border-color="rgba(20,184,166,1)"
        data-follow-section-cta-text-color="rgba(255,255,255,1)"
        data-follow-section-cta-border-radius="8px"
      ></a>
    `
  }

  const script = document.createElement('script')
  script.id = 'bandsintown-script'
  script.charset = 'utf-8'
  script.src = 'https://widgetv3.bandsintown.com/main.min.js'
  document.head.appendChild(script)
})
</script>

<style scoped>
.shows-page {
  /* Adjust based on your navbar height but only on dedicated shows page */
  min-height: auto;
}

/* 
  Custom styling for the Bandsintown widget to make it match the Sqwerv site 
  more closely and improve spacing/readability.
*/

:deep(.bit-event) {
  padding: 1.5rem 0 !important;
}

:deep(.bit-date) {
  font-size: 1.25rem !important;
  font-weight: bold !important;
  color: #14b8a6 !important; /* Sqwerv Teal */
}

:deep(.bit-venue) {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
}

:deep(.bit-location) {
  font-size: 1.125rem !important;
  color: #4b5563 !important; /* Gray-600 */
}

/* Ensure buttons don't bunch up vertically if wrapped */
:deep(.bit-button) {
  padding: 0.5rem 1.5rem !important;
  font-weight: bold !important;
  letter-spacing: 0.05em !important;
}

/* Tweak the Follow Sqwerv button */
:deep(.bit-follow-section-cta) {
  margin-top: 1rem !important;
  margin-bottom: 2rem !important;
}
</style>
